import React, { FunctionComponent } from 'react';
import { AlertTriangleIcon } from 'lucide-react';
import { GaitMlResults, UploadRequestItem } from '@companion-professional/dctypes';
import { ClipsOfInterest } from './ClipsOfInterest';
import { useGetAnimatedGifUrlsForGaitMlResult } from '../../../hooks/useGetAnimatedGifUrls';
import { Card, QueryLoaderWrapper, Text, TextLink } from '@companion-professional/components';
import { cn } from '@companion-professional/webutils';
import { ML_ANALYSIS_FAQ_URL } from '../../../lib/config';

interface GaitAnalysisProps {
  requestedItem: UploadRequestItem;
  gaitMlResult: GaitMlResults;
  goToTime: (timeInSeconds: number) => void;
}

// GaitAnalysis is a component that displays the results of a gait analysis performed on a video.
export const GaitAnalysis: FunctionComponent<GaitAnalysisProps> = ({ gaitMlResult, requestedItem, goToTime }) => {
  const { clips, error, isPending } = useGetAnimatedGifUrlsForGaitMlResult(requestedItem.item_id, gaitMlResult);

  return (
    <QueryLoaderWrapper isPending={isPending} error={error}>
      <Card variant="muted" className="justify-start gap-4 p-4">
        <div className="flex flex-col gap-2">
          {/* High confidence results are shown in the destructive color.  The theme has no text-safe orange, so medium
              confidence results only color the icon (with the brand warning yellow). */}
          <Text
            as="div"
            weight="strong"
            tone={gaitMlResult.confidence === 'high' ? 'destructive' : 'default'}
            className="flex flex-row gap-1"
          >
            <AlertTriangleIcon width={20} className={cn({ 'text-warning': gaitMlResult.confidence === 'medium' })} />
            <div>
              Computer Vision Analysis: <span className="capitalize">{gaitMlResult.confidence}</span> Confidence of
              Musculoskeletal Disorder
            </div>
          </Text>
          <Text as="div" size="sm" tone="muted" className="space-y-2">
            <p>
              Computer vision analysis was performed on the video above and the model returned a&nbsp;
              <Text as="span" size="sm" weight="strong">
                {gaitMlResult.confidence}
              </Text>{' '}
              level of confidence that the subject has a musculoskeletal disorder. More information on how to interpret
              these results can be found on our&nbsp;
              <TextLink href={ML_ANALYSIS_FAQ_URL} target="_blank" rel="noopener noreferrer">
                Computer Vision Analysis FAQ
              </TextLink>
            </p>
            {gaitMlResult.relevant_clips.length > 0 ? (
              <p>
                Below are clips that highlight specific moments in the video that the computer vision system found
                relevant to the analysis. You can click on each clip to jump to that moment in the video:
              </p>
            ) : null}
          </Text>
        </div>
        {clips ? <ClipsOfInterest clips={clips} goToTime={goToTime} /> : null}
      </Card>
    </QueryLoaderWrapper>
  );
};
